"use client"

import type { VariantProps } from "class-variance-authority"
import type { HTMLMotionProps } from "motion/react"
import { forwardRef, memo } from "react"
import { cva } from "class-variance-authority"
import { motion } from "motion/react"

import { cn } from "@productlane/lib/src/browser"

export const dotVariants = cva("relative flex", {
	variants: {
		color: {
			gray: "",
			green: "",
			red: "",
			widgetChangelogActive: "",
			widgetChangelogInactive: "",
		},
		size: {
			sm: "h-1.5 w-1.5 min-h-1.5 min-w-1.5",
			md: "h-2 w-2 min-h-2 min-w-2",
			default: "h-2.5 w-2.5 min-h-2.5 min-w-2.5",
			widget: "h-[11px] w-[11px] min-w-[11px] min-h-[11px]",
		},
	},
	defaultVariants: {
		color: "gray",
		size: "default",
	},
})

const dotPingVariants = cva(
	"absolute inline-flex h-full w-full animate-ping rounded-full",
	{
		variants: {
			color: {
				gray: "bg-gray-400/75",
				green: "bg-green-400/75",
				red: "bg-red-400/75",
				widgetChangelogActive: "bg-accent dark:bg-accent-dark",
				widgetChangelogInactive:
					"bg-primary dark:bg-secondary-dark drop-shadow-sm",
			},
		},
		defaultVariants: {
			color: "gray",
		},
	},
)
const dotInnerVariants = cva("relative inline-flex rounded-full", {
	variants: {
		color: {
			gray: "bg-gray-500",
			green: "bg-green-500",
			red: "bg-red-500",
			widgetChangelogActive:
				"bg-accent dark:bg-accent-dark ring-1 ring-accent dark:ring-accent-dark",
			widgetChangelogInactive:
				"bg-gray-50 dark:bg-gray-700 drop-shadow-sm ring-1 ring-black/15 dark:ring-white/15",
		},
		size: {
			sm: "h-1.5 w-1.5 min-h-1.5 min-w-1.5",
			md: "h-2 w-2 min-h-2 min-w-2",
			default: "h-2.5 w-2.5 min-h-2.5 min-w-2.5",
			widget: "h-[11px] w-[11px] min-w-[11px] min-h-[11px]",
		},
	},
	defaultVariants: {
		color: "gray",
		size: "default",
	},
})

interface DotProps
	extends
		Omit<HTMLMotionProps<"div">, "color">,
		VariantProps<typeof dotVariants> {
	ping?: boolean
}

export const Dot = memo(
	forwardRef<HTMLDivElement, DotProps>(
		({ className, ping = false, color, size, ...props }, ref) => {
			return (
				<motion.div
					className={cn(dotVariants({ color, size, className }), className)}
					ref={ref}
					{...props}
				>
					{ping && <span className={dotPingVariants({ color })}></span>}
					<span className={dotInnerVariants({ color, size })}></span>
				</motion.div>
			)
		},
	),
)
